import { updateMeetingRoom, useMeetingRooms } from "@/api";
import MeetingRoomLoading from "@/components/bookings/MeetingRoomLoading";
import { MeetingRoom, NotificationType } from "@/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/styles/Bookings.module.scss";
import { Breadcrumbs } from "@app-components";
import MeetingRoomForm, {
  MeetingRoomFormSubmitProps,
} from "@/components/bookings/MeetingRoomForm";
import { useSnackbarStore } from "@/store/zustandStore";
import { useQueryClient } from "@tanstack/react-query";
import { getBuildingId, notifyApiError } from "@/lib";
import { useUploadRoomImages } from "@/hooks/useUploadRoomImages";
import { useWelcomeBookingAccess } from "@/hooks/useWelcomeBookingAccess";
import PageBackButton from "@/components/PageBackButton";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";

export const Route = createFileRoute(
  "/_portal/building/settings/room/$roomId/edit",
)({
  component: EditMeetingRoom,
});

function EditMeetingRoom() {
  const { allowBuildingAdminOrReceptionistAccess } =
    useBuildingAdminOrReceptionistAccess();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { roomId } = Route.useParams();
  const navigate = useNavigate();
  const { allowWelcomeBookingAccess } = useWelcomeBookingAccess();
  const { addNotification } = useSnackbarStore();
  const uploadRoomImages = useUploadRoomImages();
  const {
    data: rooms,
    isLoading,
    isError,
  } = useMeetingRooms(allowWelcomeBookingAccess, true);
  const room = useMemo(
    () => rooms?.find((r) => r.roomId === roomId),
    [rooms, roomId],
  );
  const [submitSucceeded, setSubmitSucceeded] = useState(false);

  const breadcrumbs = [
    {
      label: t("SIDEBAR.BUILDING_SETTINGS"),
      path: "/building/settings",
    },
    {
      label: "Welcome Booking",
      path: "/building/settings/booking",
    },
    {
      label: `${t("EDIT")} ${t("BOOKINGS.MODAL.LABELS.ROOM").toLowerCase()}`,
      path: `/building/settings/room/${roomId}/edit`,
    },
  ];

  const handleOnSubmit = async ({ value }: MeetingRoomFormSubmitProps) => {
    const { thumbnail, images, ...rest } = value;
    const reqBody = rest as MeetingRoom;
    reqBody.roomId = roomId;
    reqBody.images = images.map((img) => img.previewUrl);
    const thumbnailWasUploaded = thumbnail?.encoded;
    const anyImageWasUploaded =
      reqBody.images?.length && images.some((img) => img.encoded);

    // Only newly added images carry `encoded`; the rest are already uploaded.
    const newImages = images.filter((img) => img.encoded);

    if (thumbnailWasUploaded || anyImageWasUploaded) {
      const uploaded = await uploadRoomImages({
        thumbnail: thumbnailWasUploaded ? thumbnail : null,
        images: newImages,
      });
      // User chose to go back and fix the failed images.
      if (!uploaded) return;

      // A discarded thumbnail keeps the saved one rather than clearing it.
      reqBody.thumbnail = thumbnailWasUploaded
        ? uploaded.thumbnail || room?.thumbnail || ""
        : thumbnail?.previewUrl;

      // Put the uploaded URLs back in place, dropping any the user discarded.
      let uploadedIndex = 0;
      reqBody.images = images.flatMap((img) =>
        img.encoded
          ? (uploaded.images[uploadedIndex++] ?? [])
          : [img.previewUrl],
      );
    } else {
      reqBody.thumbnail = thumbnail?.previewUrl;
    }

    try {
      const res = await updateMeetingRoom(reqBody);
      setSubmitSucceeded(true);
      addNotification(
        t(`NOTIFICATIONS.${res.data.code}`, {
          entity: t("BOOKINGS.MODAL.LABELS.ROOM").toLowerCase(),
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "meeting-rooms"],
      });
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "meeting-room-search"],
      });
      navigate({ to: "/building/settings/booking" });
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: t("BOOKINGS.MODAL.LABELS.ROOM").toLowerCase(),
        },
        fallbackKey: "UPDATE_FAILED",
      });
    }
  };

  if (isError || (rooms && !room))
    throw new Error("Failed to load meeting rooms");

  if (!allowBuildingAdminOrReceptionistAccess) {
    return null;
  }

  return isLoading ? (
    <MeetingRoomLoading />
  ) : (
    room && (
      <div className={styles.container}>
        <div className="backAndBreadcrumbsContainer">
          <PageBackButton
            fallbackPath={{ to: "/building/settings", hash: "booking" }}
          />
          <Breadcrumbs
            levels={breadcrumbs}
            onNavigate={(path) => navigate({ to: path })}
          />
        </div>
        <h1 style={{ marginBottom: "var(--wlcm-spacing-sm)" }}>
          {t("EDIT")} {t("BOOKINGS.MODAL.LABELS.ROOM").toLowerCase()} (
          {room.roomName})
        </h1>
        <MeetingRoomForm
          room={room}
          submitSucceeded={submitSucceeded}
          onSubmit={handleOnSubmit}
        />
      </div>
    )
  );
}
